import React from 'react'
import { FieldHookConfig, useField } from 'formik'
import { DateTimePicker as UIDateTimePicker } from '@carsys/ui'
import { isInvalid } from './utils'
import DateType from '@carsys/ui/src/date-helpers/date-type'
import moment from 'moment'
import { useRegisterFormField } from './TabbedForm/FormTabPanel'
import { TextFieldProps } from '@mui/material/TextField'

interface DateTimePickerProps
  extends Omit<React.ComponentProps<typeof UIDateTimePicker>, 'name'>,
    Pick<FieldHookConfig<string | null>, 'name' | 'validate'> {
  required?: boolean
}

export function DateTimePicker({ name, validate, ...props }: DateTimePickerProps) {
  const [input, meta, helpers] = useField<string | null>({ name, validate })
  const parentTextFieldProps = props.slotProps?.textField as TextFieldProps | undefined

  useRegisterFormField(name)

  const onChange = (date: DateType) => {
    // api takes date in ISO8601 format, which toJson serializes to.
    helpers.setValue(date ? date.toJSON() : null)
  }

  return (
    <UIDateTimePicker
      {...props}
      // Empty string is not a parsable dateTime
      // this causes issues with controlled form input
      // also API provides 0001-01-01T00:00:00Z as empty dateTime.
      // Null will be set as correct empty value
      value={moment(input.value)}
      onChange={onChange}
      slotProps={{
        ...props.slotProps,
        textField: {
          ...parentTextFieldProps,
          id: input.name,
          name: input.name,
          onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
            input.onBlur(e)
            parentTextFieldProps?.onBlur?.(e)
          },
          ...(parentTextFieldProps?.onKeyDown && {
            onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
              parentTextFieldProps.onKeyDown!(e)
            }
          }),
          error: isInvalid(meta),
          required: props.required,
          // @ts-ignore Fix typescript does not exist error for helperText
          // see: https://github.com/mui/mui-x/issues/8026
          helperText: isInvalid(meta) ? meta.error : parentTextFieldProps?.helperText
        }
      }}
    />
  )
}
